import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import {
  GetUserAccountsQuery,
  UserLoginErrorCode,
} from "../../generated/graphql";
import { Button } from "../Common/Button";
import { Section, Sections } from "../Common/Layout";
import { Title } from "../Common/Typography";
import { Password, Text } from "../Inputs/react-hook-form";
import { SecondaryActions } from "./CommonLoginComponents/SecondaryActions";
import { ErrorMessage } from "./ErrorMessage";
import { ProvideCredentialsNavigationCard } from "./ProvideCredentialsNavigationCard";

export type ProvideCredentialsFormProps = {
  error: Maybe<{ message: string; code?: UserLoginErrorCode }>;
  onClickPasswordReset?: () => void;
  title?: string;
  accounts?: NonNullable<GetUserAccountsQuery["getUserAccountsByEmail"]>;
  currentAccount?: Maybe<
    NonNullable<GetUserAccountsQuery["getUserAccountsByEmail"]>[0]
  >;
  onLogin: (data: ProvideCredentialsFormData) => Promise<void>;
};

export type ProvideCredentialsFormData = {
  email: string;
  password: string;
};

type ProvideCredentialsFormStructure = {
  email?: Maybe<string>;
  password?: Maybe<string>;
};

export const ProvideCredentialsForm = ({
  error,
  onClickPasswordReset,
  title,
  accounts,
  currentAccount,
  onLogin,
}: ProvideCredentialsFormProps) => {
  const { t } = useTranslation();
  const { register, handleSubmit, watch } =
    useFormContext<ProvideCredentialsFormStructure>();
  const [email, password] = watch(["email", "password"]);
  const disabled = !email || !password;

  const handleLogin = async ({
    email,
    password,
  }: ProvideCredentialsFormStructure) => {
    await onLogin({
      email: email!,
      password: password!,
    });
  };

  let actions = [];

  if (onClickPasswordReset) {
    actions.push({
      onClick: onClickPasswordReset,
      text: t("forgot-your-password"),
    });
  }

  return (
    <Sections as="form" onSubmit={handleSubmit(handleLogin)}>
      {error && (
        <ErrorMessage
          error={error}
          onClickPasswordReset={onClickPasswordReset}
        />
      )}
      <div>
        <Section>
          {title ?
            <Title type="semiBold" size="large">
              {title}
            </Title>
          : null}
          {currentAccount && accounts ?
            <ProvideCredentialsNavigationCard
              accounts={accounts}
              email={email ?? ""}
              currentAccount={currentAccount}
            />
          : <Text label={t("sign-in-email")} {...register("email")} />}
          <Password label={t("sign-in-password")} {...register("password")} />
        </Section>
        {arrayHasAtLeastOneItem(actions) && (
          <SecondaryActions actions={actions} />
        )}
      </div>
      <Button
        type="submit"
        size="medium"
        styleVariant="primary"
        disabled={disabled}
        stretch
        data-testid="sign-in-submit-button"
      >
        {t("sign-in")}
      </Button>
    </Sections>
  );
};
